import { logout } from "../../redux/actions/authActions";
import { store } from "../../redux/store";
import { showToast } from "../../utils/toast";
import { API_BASE_URL } from "./apiUrl";
import { API_URL } from "./apiUrl";

const joinUrl = (base, endpoint) => {
  const b = String(base || "").replace(/\/+$/, "");
  const e = String(endpoint || "").replace(/^\/+/, "");
  return `${b}/${e}`;
};

const buildHeaders = (extra = {}) => {
  const headers = { ...extra };
  const apiOrigin = API_URL || API_BASE_URL || "";
  if (apiOrigin.includes("ngrok")) {
    headers["ngrok-skip-browser-warning"] = "true";
  }
  return headers;
};

export const extractAuthFromLoginResponse = (response, responseData = {}) => {
  const headerToken = response.headers.get("Authorization");
  const bodyToken = responseData?.token;
  const token = headerToken || bodyToken || null;
  const id = response.headers.get("id") || responseData?.id || null;
  return { token, id };
};

// GET request
const getAuthToken = () => {
  const state = store.getState();
  return state?.auth?.user?.token || null;
};

const handleUnauthorized = () => {
  showToast("Your session has expired. Please log in again.",'error')
  store.dispatch(logout());
  localStorage.removeItem("redirectData");
  window.location.href = "/login";
};

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorized();   
      return null;
    }
    const errorDetails = await response.json();
    const errorMessages = Object.values(errorDetails).flat();
    throw new Error(errorMessages[0]);
  }
  return response;
};

export const fetchData = async (endpoint) => {
  try {
    let token = getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }
    const response = await fetch(joinUrl(API_URL, endpoint), {
      method: "GET",
      headers: buildHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    });

   return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

// fetch data without token
export const fetchDataWithoutToken = async (endpoint, requiresAuth = true) => {
  try {
    let token = getAuthToken();

    let headers = buildHeaders({
      "Content-Type": "application/json",
    });

    if (requiresAuth && token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(joinUrl(API_URL, endpoint), {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const errorDetails = await response.json();
      const errorMessages = Object.values(errorDetails).flat();

      if (errorMessages[0] === "Unauthorized Access") {
        handleUnauthorized();
        return;
      }

      throw new Error(errorMessages[0]);
    }

    return response;
  } catch (error) {
    console.error("API Error:", error);
    if (error.message === "Unauthorized") {
      handleUnauthorized();
      return;
    }
    throw error;
  }
};

export const fetchBlogData = async (
  endpoint,
  queryParams = {},
  requiresAuth = true
) => {
  try {
    let token = getAuthToken();

    let headers = {
      "Content-Type": "application/json",
    };

    if (requiresAuth && token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${joinUrl(API_URL, endpoint)}${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const errorDetails = await response.json();
      const errorMessages = Object.values(errorDetails).flat();

      if (errorMessages[0] === "Unauthorized Access") {
        handleUnauthorized();
        return;
      }

      throw new Error(errorMessages[0]);
    }

    return response;
  } catch (error) {
    console.error("API Error:", error);
    if (error.message === "Unauthorized") {
      handleUnauthorized();
      return;
    }
    throw error;
  }
};

export const fetchDataAuth = async (endpoint) => {
  try {
    let token = getAuthToken();

    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const errorDetails = await response.json();
      const errorMessages = Object.values(errorDetails).flat();

      if (errorMessages[0] === "Unauthorized Access") {
        handleUnauthorized();
        return;
      }

      throw new Error(errorMessages[0]);
    }
    return await response;
  } catch (error) {
    console.error("API Error:", error);
    if (error.message === "Unauthorized") {
      handleUnauthorized();
      return;
    }
    throw error;
  }
};

export const fetchToken = async (identity, roomName) => {
  try {
    const response = await fetch(
      `http://localhost:8081/api/auth/generateToken?identity=${identity}&roomName=${roomName}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const errorDetails = await response.json();
      const errorMessages = Object.values(errorDetails).flat();

      if (errorMessages[0] === "Unauthorized Access") {
        handleUnauthorized();
        return;
      }

      throw new Error(errorMessages[0]);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("API Error:", error);
    if (error.message === "Unauthorized") {
      handleUnauthorized();
      return;
    }
    throw error;
  }
};


//  POST request
export const postData = async (endpoint, body) => {
  let token = getAuthToken();
  const isFormData = body instanceof FormData;

  const headers = isFormData
    ? { Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "POST",
      headers,
      body: isFormData ? body : JSON.stringify(body),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  PATCH (update)
export const updateData = async (endpoint, body) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "PATCH",
      headers: buildHeaders({ Authorization: `Bearer ${token}` }),
      body,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  PUT
export const putData = async (endpoint, body) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  DELETE
export const deleteData = async (endpoint) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  DELETE with body
export const removeLike = async (endpoint, data) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  DELETE with query params
export const removeData = async (endpoint, data) => {
  let token = getAuthToken();
  const queryParams = new URLSearchParams(data).toString();

  try {
    const response = await fetch(`${joinUrl(API_BASE_URL, endpoint)}?${queryParams}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    const handled = await handleResponse(response);
    return handled ? await handled.json() : null;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

const parseErrorResponse = async (response) => {
  try {
    const errorDetails = await response.json();
    if (errorDetails?.error) {
      return typeof errorDetails.error === "string"
        ? errorDetails.error
        : String(errorDetails.error);
    }
    const errorMessages = Object.values(errorDetails).flat();
    if (errorMessages[0]) {
      return String(errorMessages[0]);
    }
  } catch {
    // ignore JSON parse errors
  }
  return `HTTP error! Status: ${response.status}`;
};

//  POST without token (sign-in, sign-up, forgot password — never redirect on 401)
export const postRequest = async (endpoint, body) => {
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "POST",
      headers: buildHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await parseErrorResponse(response));
    }
    return response;
  } catch (error) {
    console.error("API Error:", error);
    if (error?.message === "Failed to fetch") {
      throw new Error(`Unable to reach API at ${API_BASE_URL}. Check backend URL and CORS settings.`);
    }
    throw error;
  }
};

//  PATCH (appointment update)
export const updateApointmentData = async (endpoint, body) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  PATCH (status update)
export const updateStatus = async (endpoint, body) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  POST with FormData
export const postFormData = async (endpoint, formData) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  PATCH (W9 update)
export const updateWNineData = async (endpoint, body) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

//  PATCH (video update)
export const updateVideo = async (endpoint, body) => {
  let token = getAuthToken();
  try {
    const response = await fetch(joinUrl(API_BASE_URL, endpoint), {
      method: "PATCH",
      headers: buildHeaders({ Authorization: `Bearer ${token}` }),
      body,
    });

    const handled = await handleResponse(response);
    return handled ? await handled.json() : null;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};
